import type { ComponentType } from "react";
import { Archive, Database, Globe, Zap } from "lucide-react";
import { cn } from "@/lib/utils";

export const WORKLOAD_NAME = "orders-service";

export const SERVICE_NODES = {
  api: {
    icon: Globe,
    title: "API Gateway",
    detail: "/orders/v1",
  },
  lambda: {
    icon: Zap,
    title: "Lambda",
    detail: "order-processor",
  },
  dynamo: {
    icon: Database,
    title: "DynamoDB",
    detail: "tbl-orders-prod",
  },
  s3: {
    icon: Archive,
    title: "S3",
    detail: "orders-exports",
  },
} as const satisfies Record<
  string,
  {
    icon: ComponentType<{ className?: string }>;
    title: string;
    detail: string;
  }
>;

export type GraphNode = {
  id: string;
  icon: ComponentType<{ className?: string }>;
  title: string;
  detail: string;
  tone?: "default" | "accent" | "warning";
  className: string;
};

export function NodeCard({
  icon: Icon,
  title,
  detail,
  tone = "default",
  className,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  detail: string;
  tone?: "default" | "accent" | "warning";
  className?: string;
}) {
  return (
    <div
      className={cn(
        "relative rounded-md border bg-surface p-3 shadow-sm md:px-2.5 lg:p-3",
        tone === "accent" && "border-primary/35 bg-[var(--surface)]",
        tone === "warning" && "border-[var(--warning)]/45 bg-[var(--warning-bg)]/40",
        tone === "default" && "border-border",
        className,
      )}
    >
      <div className="flex min-w-0 items-start gap-1.5 lg:gap-2">
        <Icon
          className={cn(
            "mt-0.5 size-3.5 shrink-0 lg:size-4",
            tone === "accent" && "text-primary",
            tone === "warning" && "text-[var(--warning-text)]",
            tone === "default" && "text-foreground-muted",
          )}
        />
        <div className="min-w-0">
          <p className="text-sm font-medium leading-tight text-foreground">{title}</p>
          <p className="mt-1 font-mono text-[12px] leading-tight text-foreground-muted">
            {detail}
          </p>
        </div>
      </div>
    </div>
  );
}
